import { type AppointmentProposalOption } from '../../data-access/appointmentProposalOptions';

export type OfflineSyncErrors = AppointmentProposalOption['offlineSyncErrors'];

export interface OfflineSyncErrorSummary {
  badge: string;
  title: string;
  details: string[];
}

const TITLE = "This change couldn't be saved";

const toFieldLabel = (field: string) => {
  const words = field
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim();
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};

export const describeOfflineSyncError = (
  errors: OfflineSyncErrors
): OfflineSyncErrorSummary | null => {
  if (!errors) {
    return null;
  }

  if (errors.type === 'OFFLINE_SYNC_CONFLICT') {
    return {
      badge: 'Not saved',
      title: TITLE,
      details: [
        'This item is out of step with the server. Remove it and add it again, or contact support.',
      ],
    };
  }

  return {
    badge: 'Not saved',
    title: TITLE,
    details: Object.entries(errors.error).map(
      ([field, messages]) => `${toFieldLabel(field)}: ${messages.join(', ')}`
    ),
  };
};

interface LabeledOfflineSyncError {
  label: string;
  summary: OfflineSyncErrorSummary;
}

export const collectOfflineSyncErrors = (
  items: { label: string; errors: OfflineSyncErrors }[]
): LabeledOfflineSyncError[] =>
  items.flatMap(({ label, errors }) => {
    const summary = describeOfflineSyncError(errors);
    return summary ? [{ label, summary }] : [];
  });
